import { mainIconSize } from '@/constants';
import { downSize } from '@/helpers/text';
import type { DecodeStatus, InstrumentRunStatus } from '@/types';
import type { UseHoverProps } from '@floating-ui/react';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import type { BadgeVariants } from '@nautilusbio/nb-design-system/Badge';
import type { TextSizes } from '@nautilusbio/nb-design-system/helpers';
import { cssVar } from '@nautilusbio/nb-design-system/styles/css-variables';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import React, { type FC } from 'react';

import styles from './styles.module.css';

type StatusConfig = {
  accentColor?: string;
  color?: string;
  variant: BadgeVariants;
};

// One map, not two disjoint blocks: `completed` is shared by both enums under POR-308.
const statuses: Record<InstrumentRunStatus | DecodeStatus, StatusConfig> = {
  // InstrumentRunStatus
  draft: {
    variant: 'default',
  },
  submitted: {
    accentColor: cssVar.color.tertiary3,
    color: cssVar.color.lightBlue,
    variant: 'unstyled',
  },
  started: {
    variant: 'info',
  },
  // Shared by InstrumentRunStatus and DecodeStatus
  completed: {
    variant: 'success',
  },
  // DecodeStatus
  not_started: {
    variant: 'default',
  },
  pending: {
    variant: 'warning',
  },
  analyzing: {
    variant: 'info',
  },
  failed: {
    variant: 'danger',
  },
};

// Required, not cosmetic: wire values are lower_snake, so rendering `status` raw would
// put "not_started" in front of a user.
export const statusLabels: Record<InstrumentRunStatus | DecodeStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  started: 'Started',
  completed: 'Completed',
  not_started: 'Not Started',
  pending: 'Pending',
  analyzing: 'Analyzing',
  failed: 'Failed',
};

const hoverOptions: UseHoverProps = {
  delay: {
    close: 0,
    open: 1000,
  },
};

export type StatusBadgeProps = {
  className?: string;
  showIcon?: LucideIcon;
  size?: TextSizes;
  status: InstrumentRunStatus | DecodeStatus;
};

const StatusBadge: FC<StatusBadgeProps> = ({ className, showIcon, size = 'md', status }) => {
  const { accentColor, color, variant } = statuses[status];
  const label = statusLabels[status];
  const isTiny = size === 'xs3';
  const Icon = showIcon;
  const textSize = downSize(size);

  return (
    <Tooltip
      content={label}
      hoverOptions={hoverOptions}
      isDisabled={!isTiny}
      placement="left"
      trigger={
        <Badge
          accentColor={accentColor}
          className={clsx(styles.badge, isTiny && styles.sm_xs3, !!showIcon && styles.showIcon, className)}
          color={color}
          size={size}
          variant={variant}
        >
          <Text as="span" size={textSize}>
            {label}
          </Text>

          {Icon && <Icon size={mainIconSize} />}
        </Badge>
      }
    />
  );
};

// Unknown/legacy wire values pass through unchanged rather than rendering as blank. Own-property
// check, not `in`: `in` is true for inherited keys like `constructor`, which would return a
// function from a helper the callers type as string.
export const statusLabel = (value: string): string =>
  Object.hasOwn(statusLabels, value) ? statusLabels[value as InstrumentRunStatus | DecodeStatus] : value;

export default StatusBadge;
